/**
 * Side thumbnail slider for the v2 PDP (AIW and MoistureWiser):
 * - Hijacks prev/next buttons to scroll vertically AND select next/prev thumbnail
 * - Toggles disabled state + scroll fade overlays based on scroll position
 * Height is synced to the main viewer in JS (see syncHeight) on connect / resize /
 * image-load; on mobile the inline height is cleared so CSS takes over.
 */
if (customElements.get('thumbnail-slider-vertical') === undefined) {
  const DESKTOP_QUERY = '(min-width: 990px)';

  class ThumbnailSliderVertical extends HTMLElement {
    constructor() {
      super();
      this.scheduleSync = this.scheduleSync.bind(this);
      this.updateButtonState = this.updateButtonState.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.pendingTimeouts = [];
    }

    schedule(fn, delay) {
      const id = setTimeout(fn, delay);
      this.pendingTimeouts.push(id);
      return id;
    }

    connectedCallback() {
      // Scope to the media-gallery this element wraps so each <thumbnail-slider-vertical>
      // (mobile + desktop duplicate when media_position == 'right') gets its own behavior.
      this.gallery = this.querySelector('media-gallery');
      if (!this.gallery) {
        if (typeof console !== 'undefined') console.warn('[thumbnail-slider-vertical] no media-gallery found inside element');
        return;
      }

      this.slider = this.gallery.querySelector('.thumbnail-slider');
      if (!this.slider) return;

      this.viewer = this.gallery.querySelector('.slider-mobile-gutter');
      this.list = this.slider.querySelector('.thumbnail-list');
      this.prev = this.slider.querySelector('.slider-button--prev');
      this.next = this.slider.querySelector('.slider-button--next');
      if (!this.list || !this.prev || !this.next) return;

      this.mql = window.matchMedia(DESKTOP_QUERY);

      if (window.ResizeObserver && this.viewer) {
        this.resizeObserver = new ResizeObserver(this.scheduleSync);
        this.resizeObserver.observe(this.viewer);
        this.viewer.querySelectorAll('img').forEach((img) => this.resizeObserver.observe(img));
        this.resizeObserver.observe(this.gallery);
      }

      window.addEventListener('resize', this.scheduleSync);
      this.mql.addEventListener('change', this.scheduleSync);

      if (this.viewer) {
        this.viewer.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.scheduleSync, { once: true });
        });
      }
      this.schedule(() => this.syncHeight(), 100);
      this.schedule(() => this.syncHeight(), 500);
      this.schedule(() => this.syncHeight(), 1500);

      this.prev.addEventListener('click', this.onPrevClick, true);
      this.next.addEventListener('click', this.onNextClick, true);

      this.list.addEventListener('scroll', this.updateButtonState);

      this.mutationObserver = new MutationObserver(this.updateButtonState);
      this.mutationObserver.observe(this.list, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-current'],
      });

      this.schedule(this.updateButtonState, 100);
      this.schedule(this.updateButtonState, 500);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      window.removeEventListener('resize', this.scheduleSync);
      if (this.mql) this.mql.removeEventListener('change', this.scheduleSync);
      if (this.prev) this.prev.removeEventListener('click', this.onPrevClick, true);
      if (this.next) this.next.removeEventListener('click', this.onNextClick, true);
      if (this.list) this.list.removeEventListener('scroll', this.updateButtonState);
      this.pendingTimeouts.forEach(clearTimeout);
      this.pendingTimeouts = [];
    }

    isDesktop() {
      return this.mql && this.mql.matches;
    }

    syncHeight() {
      if (!this.isDesktop()) {
        this.slider.style.height = '';
        return;
      }
      if (!this.viewer) return;
      const h = this.viewer.offsetHeight;
      if (h > 0) this.slider.style.height = h + 'px';
    }

    scheduleSync() {
      requestAnimationFrame(() => this.syncHeight());
    }

    getThumbnails() {
      return Array.from(this.list.querySelectorAll('.thumbnail-list__item'));
    }

    getActiveIndex(thumbs) {
      for (let i = 0; i < thumbs.length; i++) {
        if (thumbs[i].querySelector('button[aria-current="true"]')) return i;
      }
      return 0;
    }

    selectAdjacent(direction) {
      const thumbs = this.getThumbnails();
      if (!thumbs.length) return;
      const currentIdx = this.getActiveIndex(thumbs);
      const nextIdx = currentIdx + direction;
      if (nextIdx < 0 || nextIdx >= thumbs.length) return;
      const targetButton = thumbs[nextIdx].querySelector('button');
      if (targetButton) {
        targetButton.click();
        thumbs[nextIdx].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    onPrevClick(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.selectAdjacent(-1);
    }

    onNextClick(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.selectAdjacent(1);
    }

    updateButtonState() {
      const thumbs = this.getThumbnails();
      const currentIdx = this.getActiveIndex(thumbs);
      const scrolled = this.list.scrollTop > 5;
      const atEnd = this.list.scrollTop + this.list.clientHeight >= this.list.scrollHeight - 5;
      this.prev.toggleAttribute('disabled', currentIdx <= 0);
      this.next.toggleAttribute('disabled', currentIdx >= thumbs.length - 1);
      this.slider.toggleAttribute('data-can-scroll-up', scrolled);
      this.slider.toggleAttribute('data-can-scroll-down', !atEnd);
    }
  }

  customElements.define('thumbnail-slider-vertical', ThumbnailSliderVertical);
}
